<template>
  <Loader
    v-if="isLoading"
    style="z-index:1102"
  />

  <ConfirmPopup group="basicGroup" />

  <div class="mb-4 alert-info">
    {{ t('storeParameters.services.salesAssociationServiceParameter.infoBubble') }}
  </div>

  <div class="tabs-wrapper">
    <button
      v-if="!globalSave"
      type="button"
      class="reset-link"
      :disabled="resetLoading"
      @click="confirmReset"
    >
      <i class="fa-regular fa-rotate-left" />
      <span>{{ t('storeParameters.services.salesAssociationServiceParameter.resetDefaults') }}</span>
    </button>
    <TabView
      :active-index="activeTab"
      class="sales-association-tabs"
      @tab-change="({ index }) => { activeTab = index }"
    >
    <TabPanel
      v-for="globalType in globalTypes"
      :key="globalType"
      :header="t(`storeParameters.services.salesAssociationServiceParameter.tabs.${globalType}`)"
    >
      <div class="channel-cards">
        <div
          v-for="media in mediaList"
          :key="`${globalType}-${media.key}`"
          class="channel-card"
        >
          <div class="channel-card__icon">
            <i :class="media.associationIcon" />
          </div>
          <div class="channel-card__title">
            {{ t(`storeParameters.services.salesAssociationServiceParameter.channels.${media.key}`) }}
          </div>
          <div class="channel-card__fields">
            <div class="field-group">
              <label class="field-group__label">
                {{ t('storeParameters.services.salesAssociationServiceParameter.fields.delay') }}
              </label>
              <div class="delay-inputs">
                <BaseInputNumber
                  v-model="configurations[globalType][media.key].delay.value"
                  :min="getMin(configurations[globalType][media.key].delay.unit)"
                  :max="getMax(configurations[globalType][media.key].delay.unit)"
                  :input-width="'3.5rem'"
                  @update:model-value="onDelayInputHandler(globalType, media.key)"
                />
                <Dropdown
                  v-model="configurations[globalType][media.key].delay.unit"
                  :options="unitOptions"
                  option-label="label"
                  option-value="value"
                  class="unit-select"
                  @change="onUnitChange(globalType, media.key)"
                />
              </div>
            </div>
            <div class="field-group">
              <label class="field-group__label">
                {{ t('storeParameters.services.salesAssociationServiceParameter.fields.action') }}
              </label>
              <Dropdown
                v-if="media.supportsOpenClick"
                v-model="configurations[globalType][media.key].action"
                :options="actionOptions"
                option-label="label"
                option-value="value"
                class="action-select"
              />
              <div
                v-else
                class="action-static"
              >
                {{ t('storeParameters.services.salesAssociationServiceParameter.actions.click') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TabPanel>
  </TabView>
  </div>

  <div
    v-if="!globalSave"
    class="mt-4"
  >
    <SpmButton
      :label="t('save')"
      :loading="saveLoading"
      class="p-button p-button-primary w-full"
      @click="handleSave"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  defineExpose,
  reactive,
  ref,
  computed,
  onMounted,
} from 'vue';

import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Dropdown from 'primevue/dropdown';
import ConfirmPopup from 'primevue/confirmpopup';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import Loader from '@/components/layout/Loader.vue';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';

import { showToastError, showToastSuccess } from '@/helpers';
import {
  fetchShopConfigurationsNest,
  saveShopConfigurationsNest,
  saveShopParamsOnRedis,
} from '@/composables/shop/ShopsConfiguration';
import { getSalesAssociationChannels, SendingChannel } from '@/configs/sending-channels';

import { useConfirm } from 'primevue/useconfirm';
import { useI18n } from 'vue-i18n';

type DelayUnit = 'hours' | 'days';
type DelayAction = 'open_click' | 'click';
type GlobalType = 'automation' | 'bulk';

interface SalesAssociationDelay {
  delay: {
    value: number;
    unit: DelayUnit;
  };
  action: DelayAction;
}

const GLOBAL_TYPES: GlobalType[] = ['automation', 'bulk'];

const MAX_HOURS = 5 * 24;
const MAX_DAYS = 5;
const MIN_HOURS = 1;
const MIN_DAYS = 1;

const buildDefaults = (
  channels: SendingChannel[],
): Record<GlobalType, Record<string, SalesAssociationDelay>> => {
  const create = (): Record<string, SalesAssociationDelay> => channels.reduce((acc, channel) => {
    acc[channel.key] = {
      delay: { value: 5, unit: 'days' },
      action: channel.supportsOpenClick ? 'open_click' : 'click',
    };
    return acc;
  }, {} as Record<string, SalesAssociationDelay>);

  return {
    automation: create(),
    bulk: create(),
  };
};

const buildKey = (globalType: GlobalType, mediaKey: string) => `shopRequiredDataOrdersAssociationConfiguration_${globalType}_${mediaKey}`;

export default defineComponent({
  name: 'SalesAssociationServiceParameter',

  components: {
    TabView,
    TabPanel,
    Dropdown,
    ConfirmPopup,
    SpmButton,
    Loader,
    BaseInputNumber,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    globalSave: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['close'],

  setup(props) {
    const { t } = useI18n();
    const confirm = useConfirm();

    const isLoading = ref(false);
    const saveLoading = ref(false);
    const resetLoading = ref(false);
    const activeTab = ref(0);

    const channels: SendingChannel[] = getSalesAssociationChannels();

    const configurations = reactive(buildDefaults(channels));

    const allConfigKeys = (): string[] => {
      const keys: string[] = [];
      GLOBAL_TYPES.forEach((globalType) => {
        channels.forEach((channel) => {
          keys.push(buildKey(globalType, channel.key));
        });
      });
      return keys;
    };

    const applyDefaults = () => {
      const defaults = buildDefaults(channels);
      GLOBAL_TYPES.forEach((globalType) => {
        channels.forEach((channel) => {
          const target = configurations[globalType][channel.key];
          const def = defaults[globalType][channel.key];
          target.delay.value = def.delay.value;
          target.delay.unit = def.delay.unit;
          target.action = def.action;
        });
      });
    };

    const confirmReset = (event: Event) => {
      confirm.require({
        group: 'basicGroup',
        target: (event as MouseEvent).currentTarget as HTMLElement,
        message: t('storeParameters.services.salesAssociationServiceParameter.resetConfirmMessage'),
        header: t('storeParameters.services.salesAssociationServiceParameter.resetConfirmHeader'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-secondary',
        accept: async () => {
          resetLoading.value = true;
          try {
            applyDefaults();
            const result = await saveShopConfigurationsNest(props.shopId, buildPayload());
            if (!result) {
              showToastError('GENERIC_ERROR');
              return;
            }
            await saveShopParamsOnRedis(props.shopId);
            showToastSuccess('savedSuccessful');
          } catch (err) {
            showToastError('GENERIC_ERROR');
          } finally {
            resetLoading.value = false;
          }
        },
      });
    };

    const unitOptions = computed(() => [
      { label: t('storeParameters.services.salesAssociationServiceParameter.units.hours'), value: 'hours' },
      { label: t('storeParameters.services.salesAssociationServiceParameter.units.days'), value: 'days' },
    ]);

    const actionOptions = computed(() => [
      { label: t('storeParameters.services.salesAssociationServiceParameter.actions.open_click'), value: 'open_click' },
      { label: t('storeParameters.services.salesAssociationServiceParameter.actions.click'), value: 'click' },
    ]);

    const getMin = (unit: DelayUnit) => (unit === 'hours' ? MIN_HOURS : MIN_DAYS);
    const getMax = (unit: DelayUnit) => (unit === 'hours' ? MAX_HOURS : MAX_DAYS);

    const clamp = (value: number, unit: DelayUnit) => {
      const min = getMin(unit);
      const max = getMax(unit);
      if (Number.isNaN(value) || value < min) return min;
      if (value > max) return max;
      return Math.floor(value);
    };

    const onDelayInputHandler = (globalType: GlobalType, mediaKey: string) => (value: number) => {
      const target = configurations[globalType][mediaKey];
      target.delay.value = clamp(value, target.delay.unit);
    };

    const onUnitChange = (globalType: GlobalType, mediaKey: string) => {
      const target = configurations[globalType][mediaKey];
      target.delay.value = clamp(target.delay.value, target.delay.unit);
    };

    const buildPayload = () => {
      const out: Array<{ key: string; value: string; lang: string | null }> = [];
      GLOBAL_TYPES.forEach((globalType) => {
        channels.forEach((channel) => {
          const cfg = configurations[globalType][channel.key];
          out.push({
            key: buildKey(globalType, channel.key),
            value: JSON.stringify({
              delay: {
                value: clamp(cfg.delay.value, cfg.delay.unit),
                unit: cfg.delay.unit,
              },
              action: cfg.action,
            }),
            lang: null,
          });
        });
      });
      return out;
    };

    const handleSave = async () => {
      const payload = buildPayload();

      if (props.globalSave) {
        return {
          err: null,
          configs: payload.map((c) => ({ key: c.key, value: c.value, lang: c.lang || '' })),
        };
      }

      saveLoading.value = true;
      try {
        const result = await saveShopConfigurationsNest(props.shopId, payload);
        if (result) {
          await saveShopParamsOnRedis(props.shopId);
          showToastSuccess('savedSuccessful');
        } else {
          showToastError('GENERIC_ERROR');
        }
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        saveLoading.value = false;
      }
      return undefined;
    };

    defineExpose({ handleSave });

    onMounted(async () => {
      try {
        isLoading.value = true;
        const data = await fetchShopConfigurationsNest(props.shopId, allConfigKeys());
        if (!data) return;

        GLOBAL_TYPES.forEach((globalType) => {
          channels.forEach((channel) => {
            const stored = data[buildKey(globalType, channel.key)];
            if (!stored) return;

            const parsed = typeof stored === 'string' ? safeJsonParse(stored) : stored;
            if (!parsed || typeof parsed !== 'object') return;

            const target = configurations[globalType][channel.key];
            const unit: DelayUnit = parsed.delay?.unit === 'hours' ? 'hours' : 'days';
            const value = Number(parsed.delay?.value);
            target.delay.unit = unit;
            target.delay.value = clamp(Number.isFinite(value) ? value : 5, unit);
            if (!channel.supportsOpenClick) {
              target.action = 'click';
            } else {
              target.action = parsed.action === 'click' ? 'click' : 'open_click';
            }
          });
        });
      } catch (err) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    function safeJsonParse(value: string) {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }

    return {
      t,
      isLoading,
      saveLoading,
      resetLoading,
      activeTab,
      configurations,
      globalTypes: GLOBAL_TYPES,
      mediaList: channels,
      unitOptions,
      actionOptions,
      getMin,
      getMax,
      onDelayInputHandler,
      onUnitChange,
      handleSave,
      confirmReset,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.tabs-wrapper {
  position: relative;
}

.alert-info {
  color: #666;
  background-color: #f6f6f6;
  padding: 1rem 1.25rem;
  border-radius: .5rem;
  border: none;
  font-size: 0.9rem;
  line-height: 1.5;
}

.reset-link {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 2.5rem;
  background: transparent;
  border: none;
  padding: 0 0.5rem;
  color: $mid-grey;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  i {
    font-size: 0.875rem;
  }

  &:hover:not(:disabled) {
    color: $brand-color-primary;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

@media (max-width: 768px) {
  .reset-link {
    position: static;
    margin-bottom: 0.5rem;
  }
}

.sales-association-tabs {
  :deep(.p-tabview-nav) {
    border-bottom: 1px solid $solitude;
  }
}

.channel-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 0.5rem;
}

.channel-card {
  display: grid;
  grid-template-columns: 48px 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: $white;
  border: 1px solid $solitude;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: $brand-color-primary;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &__icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 8px;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: $brand-color-primary;
    }
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 500;
    color: $tuna;
  }

  &__fields {
    display: flex;
    align-items: flex-end;
    gap: 1rem;
  }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  &__label {
    font-size: 0.7rem;
    color: $mid-grey;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}

.delay-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.unit-select {
  min-width: 7rem;
}

.action-select {
  min-width: 14rem;
}

.action-static {
  min-width: 14rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  color: $mid-grey;
  font-size: 0.875rem;
  font-style: italic;
}

@media (max-width: 768px) {
  .channel-card {
    grid-template-columns: 48px 1fr;
    grid-template-rows: auto auto;
    row-gap: 0.75rem;

    &__fields {
      grid-column: 1 / -1;
      flex-wrap: wrap;
    }
  }

  .delay-inputs,
  .action-select {
    width: 100%;
    min-width: 0;
  }
}
</style>
